"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { 
  LayoutDashboard, Activity, Video, FileText, 
  Bell, Server, Mic, Menu, X
} from 'lucide-react';
import './dashboard.css';

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const tabs = [ 
    { id: '/dashboard', icon: LayoutDashboard, label: 'Herd Overview' },
    { id: '/dashboard/tracking', icon: Video, label: 'Form & Posture' },
    { id: '/dashboard/acoustic', icon: Mic, label: 'Acoustic Monitoring' },
    { id: '/dashboard/advisory', icon: FileText, label: 'Vet Advisory (RAG)' },
    { id: '/dashboard/edge', icon: Server, label: 'Edge Nodes' },
  ];

  const activeTabLabel = tabs.find(t => t.id === pathname)?.label || 'Dashboard';

  return (
    <div className="dash-wrapper">
      
      {/* Mobile Overlay */}
      <div 
        className={`dash-sidebar-mobile-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Sidebar Navigation */}
      <div className={`dash-sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        <div style={{ padding: '0 24px', marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link href="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src="/assets/img/keeneye-white.png" alt="KeenEye Icon" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
              <span style={{ fontSize: '18px', fontWeight: '700', letterSpacing: '0.5px' }}>KeenEye OS</span>
            </div>
          </Link>
          <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(false)}>
            <X size={24} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '0 12px' }}>
          {tabs.map(item => (
            <Link 
              key={item.id}
              href={item.id}
              className={`dash-nav-link ${pathname === item.id ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <item.icon size={18} />
              {item.label}
            </Link>
          ))}
        </div>

        <div style={{ marginTop: 'auto', padding: '0 24px' }}>
          <div style={{ padding: '16px', backgroundColor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#34d399', boxShadow: '0 0 8px #34d399' }} />
              <span style={{ color: '#34d399', fontSize: '13px', fontWeight: '600' }}>System Online</span>
            </div>
            <div style={{ fontSize: '11px', color: '#64748b' }}>MQTT Broker Connected</div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="dash-main">
        
        {/* Top Header */}
        <header className="dash-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(true)}>
              <Menu size={24} />
            </button>
            <h2 style={{ fontSize: '20px', fontWeight: '600', textTransform: 'capitalize', margin: 0 }}>
              {activeTabLabel}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ position: 'relative' }}>
              <Bell size={20} color="#64748b" />
              <div style={{ position: 'absolute', top: '-2px', right: '-2px', width: '8px', height: '8px', backgroundColor: '#ef4444', borderRadius: '50%' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '20px', borderLeft: '1px solid #e2e8f0' }}>
              <img src="https://i.pravatar.cc/150?img=33" style={{ width: '36px', height: '36px', borderRadius: '50%' }} alt="User" />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>Dr. Sarah Jenkins</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Lead Veterinarian</div>
              </div>
            </div>
          </div>
        </header>

        {/* Route Content */}
        <div className="dash-content">
          {children}
        </div>
      </div>
    </div>
  );
}
